import { ApolloProvider } from '@apollo/client';
import { ChakraProvider, Flex, Spinner } from '@chakra-ui/react';
import { PropsWithChildren, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import client from './apolloClient';
import EmailVerification from './components/Auth/EmailVerification';
import LogIn from './components/Auth/LogIn';
import { RequireEmailVerification } from './components/Auth/RequireEmailVerification';
import { Conversations } from './components/Chat/Conversations';
import { ConversationViewRoute } from './components/Chat/ConversationViewRoute';
import { Dashboard } from './components/Dashboard/Dashboard';
import { DigitalTwins } from './components/DigitalTwins/DigitalTwins';
import { DigitalTwinViewRoute } from './components/DigitalTwins/DigitalTwinViewRoute';
import { FullPageLayout } from './components/Layouts/FullPageLayout';
import { SidebarLayout } from './components/Layouts/SidebarLayout';
import { Projects } from './components/Projects/Projects';
import { ProjectViewRoute } from './components/Projects/ProjectViewRoute';
import { Requests } from './components/Requests/Requests';
import { Settings } from './components/Settings/Settings';
import { Pricing } from './components/Pricing/Pricing';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './theme';
import { initializeAuthListener } from './utils/auth';
import SentryProvider from './contexts/SentryProvider';
import { HelivanStatsigProvider } from './contexts/StatsigProvider';
import { useStatsigClient } from '@statsig/react-bindings';
import { InternalTooling } from './components/InternalTooling/InternalTooling';
import { SocialMedia } from './components/SocialMedia/SocialMedia';
import { Audience } from './components/Audience/Audience';
import { Surveys } from './components/Surveys/Surveys';
import { SurveyResponses } from './components/Surveys/SurveyResponses';
import { SurveyCreateNew } from './components/Surveys/SurveyCreateNew';
import { SurveyGroupView } from './components/Surveys/SurveyGroupView';
import { Evaluations } from './components/Evaluations/Evaluations';
import { AcceptInvitePage } from './components/Settings/AcceptInvitePage';
import { AgentDetailsView } from './components/MyAgents';
import { DemoWatchView } from './components/Projects/demo/DemoWatchView';

const App = () => {
  useEffect(() => {
    // Initialize Firebase auth listener
    initializeAuthListener();
  }, []);

  return (
    <BrowserRouter>
      <VariousProviders>
        <AppRoutes />
      </VariousProviders>
    </BrowserRouter>
  );
};

const VariousProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <SentryProvider>
      <AuthProvider>
        <StatsigWrapper>
          <ChakraProvider theme={theme}>
            <ApolloProvider client={client}>{children}</ApolloProvider>
          </ChakraProvider>
        </StatsigWrapper>
      </AuthProvider>
    </SentryProvider>
  );
};

// Wrapper component to handle Statsig conditional rendering
const StatsigWrapper = ({ children }: { children: React.ReactNode }) => {
  const { backendUser, loading: authLoading } = useAuth();

  if (!backendUser || authLoading) {
    return <>{children}</>;
  }

  // Key on the user only — NOT the org. The org id is briefly absent during auth
  // hydration on a hard refresh, and including it here would flip the key and remount
  // the entire app (through a full-page Statsig spinner) → a visible flash/reload.
  // Org changes are handled without a remount inside HelivanStatsigProvider (and org
  // switching hard-reloads the page anyway).
  const statsigKey = backendUser.id;

  return (
    <HelivanStatsigProvider key={statsigKey}>{children}</HelivanStatsigProvider>
  );
};

// Auth protection wrapper
const RequireAuth = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Flex
        width="full"
        height="full"
        minHeight="600px"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Flex>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login but remember where they were trying to go
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Handles login flow redirects
const AuthWatcher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, isEmailVerified } = useAuth();

  useEffect(() => {
    // Only execute this logic when loading is complete
    if (loading) {
      return;
    }

    // Handle login flow redirects
    if (isAuthenticated && !loading) {
      // If user is on login page and authenticated redirect to proper verification step
      if (location.pathname === '/login') {
        // Check for pending invite token first
        const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
        if (pendingInviteToken && isEmailVerified) {
          navigate(`/accept-invite?token=${pendingInviteToken}`, { replace: true });
          return;
        }

        // Check if email was just verified (from clicking verification link)
        const emailJustVerified = sessionStorage.getItem('emailJustVerified');
        if (emailJustVerified) {
          sessionStorage.removeItem('emailJustVerified');
          // Check again for pending invite after verification
          if (pendingInviteToken) {
            navigate(`/accept-invite?token=${pendingInviteToken}`, { replace: true });
          } else {
            navigate('/agents', { replace: true });
          }
          return;
        }

        if (!isEmailVerified) {
          navigate('/verify-email', { replace: true });
        } else {
          navigate('/agents', { replace: true });
        }
      }
    }
  }, [
    isAuthenticated,
    loading,
    isEmailVerified,
    location.pathname,
    navigate,
  ]);

  return null;
};

const AppRoutes = () => {
  const { isAuthenticated, isEmailVerified } = useAuth();
  const { checkGate } = useStatsigClient();
  const isSuperAdmin = checkGate('super_admin');
  const seeSurveys = checkGate('see_surveys');

  return (
    <>
      {/* Always present component that watches for auth state changes */}
      <AuthWatcher />

      <Routes>
        {/* Public routes - accessible to everyone */}
        <Route path="/" element={<FullPageLayout />}>
          {/* No auth check here to prevent fighting with LogIn component's own redirects */}
          <Route path="login" element={<LogIn />} />

          {/* Email verification route - handles both public verification (with oobCode) and auth-required status page */}
          <Route
            path="verify-email"
            element={<EmailVerification />}
          />

          {/* Accept invitation route - accessible with or without auth */}
          <Route
            path="accept-invite"
            element={<AcceptInvitePage />}
          />

          {/* Default redirect based on auth status. Signed-out visitors land on
              the public org's Watches (read-only), not the login page. */}
          <Route
            index
            element={
              isAuthenticated ? (
                isEmailVerified ? (
                  <Navigate to="/agents" replace />
                ) : (
                  <Navigate to="/verify-email" replace />
                )
              ) : (
                <Navigate to="/projects" replace />
              )
            }
          />
        </Route>

        {/* Public read-only routes - viewable without signing in. These render
            the shell but skip RequireAuth; the resolvers serve PUBLIC-org data
            only and all mutating actions are hidden when not authenticated. */}
        <Route element={<FullPageLayout />}>
          <Route element={<SidebarLayout />}>
            <Route path="projects" element={<Projects />} />
            <Route path="projects/demo" element={<DemoWatchView />} />
            <Route path="projects/:projectId" element={<ProjectViewRoute />} />
            <Route path="personas" element={<DigitalTwins />} />
            <Route
              path="personas/:digitalTwinId"
              element={<DigitalTwinViewRoute />}
            />
            <Route path="agents" element={<DigitalTwins />} />
          </Route>
        </Route>

        {/* Protected routes - require authentication and email verification */}
        <Route element={<FullPageLayout />}>
          <Route
            element={
              <RequireAuth>
                <RequireEmailVerification>
                  <SidebarLayout />
                </RequireEmailVerification>
              </RequireAuth>
            }
          >
            {isSuperAdmin && (
              <Route path="internal-tooling" element={<InternalTooling />} />
            )}
            {isSuperAdmin && (
              <Route path="social-media" element={<SocialMedia />} />
            )}
            {(isSuperAdmin || seeSurveys) && (
              <Route path="surveys" element={<Surveys />} />
            )}
            {(isSuperAdmin || seeSurveys) && (
              <Route path="surveys/create-new" element={<SurveyCreateNew />} />
            )}
            {(isSuperAdmin || seeSurveys) && (
              <Route
                path="surveys/group/:groupId"
                element={<SurveyGroupView />}
              />
            )}
            {(isSuperAdmin || seeSurveys) && (
              <Route path="surveys/:surveyId" element={<SurveyResponses />} />
            )}
            {isSuperAdmin && <Route path="audience" element={<Audience />} />}
            <Route path="home" element={<Navigate to="/agents" replace />} />
            <Route path="settings" element={<Settings />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="conversations" element={<Conversations />} />
            <Route
              path="conversations/:conversationId"
              element={<ConversationViewRoute />}
            />
            {isSuperAdmin && (
              <Route path="evaluations" element={<Evaluations />} />
            )}
            {isSuperAdmin && (
              <Route
                path="evaluations/:projectId"
                element={<ProjectViewRoute />}
              />
            )}
            <Route path="requests" element={<Requests />} />
            <Route path="agents/:id" element={<AgentDetailsView />} />
          </Route>
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

export default App;
